import { type Component, Show, createMemo } from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import { DropdownMenu } from "@kobalte/core/dropdown-menu";
import { LogIn, LogOut, Heart, User, Sun, Moon } from "lucide-solid";
import { theme, toggleTheme } from "../lib/theme";
import { isLoggedIn, signOut, currentUser } from "../lib/auth";
import { Avatar, AvatarFallback } from "./ui/avatar";

const Header: Component = () => {
  const navigate = useNavigate();

  const handleLogout = () => {
    signOut();
    navigate("/");
  };

  const initial = createMemo(() => {
    const u = currentUser();
    const name = u?.displayName || u?.username || "";
    return name.trim().charAt(0).toUpperCase() || "U";
  });

  return (
    <header class="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur">
      <div class="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
        <A href="/" class="flex items-center gap-2 no-underline">
          <img src="/icon.svg" alt="Snippland" class="h-7 w-7" />
          <span class="text-base font-semibold text-foreground">Snippland</span>
        </A>

        <div class="flex items-center gap-2">
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-muted text-foreground transition-colors hover:bg-accent"
            aria-label="Temayı değiştir"
            title={theme() === "dark" ? "Light moda geç" : "Dark moda geç"}
            onClick={toggleTheme}
          >
            <Show when={theme() === "dark"} fallback={<Moon size={16} />}>
              <Sun size={16} />
            </Show>
          </button>

          <Show
            when={isLoggedIn()}
            fallback={
              <A
                href="/sign-in"
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-foreground no-underline transition-colors hover:bg-accent"
              >
                <LogIn size={16} />
                Giriş Yap
              </A>
            }
          >
            <DropdownMenu gutter={8}>
              <DropdownMenu.Trigger
                class="rounded-full outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="Profil menüsü"
              >
                <Avatar class="size-9">
                  <AvatarFallback class="bg-primary text-sm font-semibold text-primary-foreground">
                    {initial()}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content class="z-30 min-w-44 origin-[var(--kb-menu-content-transform-origin)] animate-[fadeIn_120ms_ease-out] rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg outline-none">
                  <div class="px-3 py-2 text-xs text-muted-foreground">
                    @{currentUser()?.username}
                  </div>
                  <DropdownMenu.Item
                    class="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground outline-none transition-colors hover:bg-accent data-[highlighted]:bg-accent"
                    onSelect={() => navigate("/profile")}
                  >
                    <User size={16} /> Profil
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    class="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground outline-none transition-colors hover:bg-accent data-[highlighted]:bg-accent"
                    onSelect={() => navigate("/likes")}
                  >
                    <Heart size={16} /> Likes
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator class="my-1 h-px bg-border" />
                  <DropdownMenu.Item
                    class="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive outline-none transition-colors hover:bg-destructive/10 data-[highlighted]:bg-destructive/10"
                    onSelect={handleLogout}
                  >
                    <LogOut size={16} /> Çıkış Yap
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu>
          </Show>
        </div>
      </div>
    </header>
  );
};

export default Header;
